import React, { useEffect, useState } from "react";
import { Input } from "../ui";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader2, CheckCircle2 } from "lucide-react";
import { PASSWORD_RULE_TEXT } from "../../utils/passwordRule";
import { useTheme } from "../../context/ThemeContext";
import {
  getPageBackground,
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
} from "../../utils/themeUtils";
import {
  lookupMembershipInvitation,
  acceptMembershipInvitation,
  InvitationPreview,
} from "../../api/membershipUsersAPI";

const AcceptMembershipInvitePage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { token, clubid } = useParams<{ token: string; clubid: string }>();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError("Missing invitation token.");
        setLoading(false);
        return;
      }
      const res = await lookupMembershipInvitation(token);
      if (res.success && res.data) {
        setPreview(res.data);
        setFirstName(res.data.invited_first_name || "");
        setLastName(res.data.invited_last_name || "");
      } else {
        setError(res.error || "Could not load invitation.");
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    // No length check here: someone who already has a login links it with
    // their existing password, whatever rule it was made under. A NEW login
    // is held to the one password rule by the server, which says what is
    // missing.
    if (!password) {
      toast.error("Enter a password.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    const res = await acceptMembershipInvitation(token, {
      password,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
    });
    setSubmitting(false);
    if (res.success) {
      setAccepted(true);
      toast.success("Account created. You can now sign in.");
      setTimeout(() => {
        navigate(`/${clubid}/login`);
      }, 1500);
    } else {
      toast.error(res.error || "Failed to accept invitation");
    }
  };

  return (
    <div className={`min-h-screen ${getPageBackground(isDarkMode)} flex items-center justify-center px-4 py-8`}>
      <div className={`w-full max-w-md rounded-lg ${getCardBackground(isDarkMode)} border ${getLightBorder(isDarkMode)} shadow-sm p-6`}>
        {loading && (
          <p className={`text-center py-8 ${getMutedText(isDarkMode)}`}>
            <Loader2 className="inline w-5 h-5 animate-spin mr-2" />
            Loading invitation…
          </p>
        )}

        {!loading && error && (
          <>
            <h1 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)}`}>Invitation unavailable</h1>
            <p className={`mt-2 text-sm ${getSecondaryText(isDarkMode)}`}>{error}</p>
            <button
              onClick={() => navigate(`/${clubid || ""}`)}
              className="mt-4 px-3 py-2 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white"
            >
              Go home
            </button>
          </>
        )}

        {!loading && !error && preview && !accepted && (
          <>
            <h1 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)}`}>
              Accept your invitation
            </h1>
            <p className={`mt-2 text-sm ${getSecondaryText(isDarkMode)}`}>
              You've been invited to help with{" "}
              <strong>{preview.family_name || "a family"}</strong>'s account at this club. You'll be able to view and manage things together (and pay for what you've agreed to cover).
            </p>
            <p className={`mt-1 text-xs ${getMutedText(isDarkMode)}`}>
              Email: <strong>{preview.invited_email}</strong>
              {preview.expires_at && (
                <> · Expires {new Date(preview.expires_at).toLocaleDateString()}</>
              )}
            </p>

            <form onSubmit={handleSubmit} className="mt-5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Input
                    label="First name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    label="Last name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Input
                  label="Password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <p className={`text-xs mt-1 ${getMutedText(isDarkMode)}`}>
                  New password: {PASSWORD_RULE_TEXT} If you already have a Club Central login with this email, use that same password.
                </p>
              </div>
              <div>
                <Input
                  label="Confirm password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-4 py-2 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
                    Creating account…
                  </>
                ) : (
                  "Accept and create account"
                )}
              </button>
            </form>
          </>
        )}

        {accepted && (
          <div className="text-center py-4">
            <CheckCircle2 className="inline w-10 h-10 text-green-500 mb-2" />
            <p className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>You're in!</p>
            <p className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`}>
              Redirecting you to sign in…
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptMembershipInvitePage;
